var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
export class KRDialogHeader extends LitElement {
    render() {
        return html `<slot></slot>`;
    }
}
KRDialogHeader.styles = css `
    :host {
      display: flex;
      padding: 0 24px;
      align-items: center;
      height: 64px;
      flex-shrink: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    ::slotted(h2) {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }
  `;
export class KRDialogContent extends LitElement {
    render() {
        return html `<slot></slot>`;
    }
}
KRDialogContent.styles = css `
    :host {
      display: block;
      padding: 24px;
      overflow: auto;
      flex: 1 1 auto;
    }
  `;
export class KRDialogFooter extends LitElement {
    render() {
        return html `<slot></slot>`;
    }
}
KRDialogFooter.styles = css `
    :host {
      display: flex;
      padding: 0 24px;
      align-items: center;
      height: 64px;
      flex-shrink: 0;
      border-top: 1px solid rgba(0, 0, 0, 0.1);
      gap: 12px;
      justify-content: flex-end;
    }
  `;
/**
 * A reference to an open dialog instance.
 *
 * Used to close the dialog and retrieve its result.
 *
 * @typeParam R - The type of the result returned when the dialog closes
 */
export class KRDialogRef {
    constructor() {
        this._resolvePromise = null;
        this._dialogElement = null;
        this._promise = new Promise((resolve) => {
            this._resolvePromise = resolve;
        });
    }
    /** @internal */
    setDialogElement(el) {
        this._dialogElement = el;
    }
    /**
     * Closes the dialog and resolves with the given result.
     *
     * @param result - The result to return to the caller
     */
    close(result) {
        if (this._resolvePromise) {
            this._resolvePromise(result);
            this._resolvePromise = null;
        }
        this._dialogElement?.remove();
    }
    /**
     * Returns a promise that resolves when the dialog is closed.
     *
     * @returns A promise that resolves with the dialog result, or undefined if dismissed
     */
    afterClosed() {
        return this._promise;
    }
}
/**
 * Generic dialog component that supports both declarative and programmatic usage.
 *
 * Declarative: place `<kr-dialog>` in HTML with slotted content and call `.open()` / `.close()`.
 * Programmatic: use `KRDialog.open(Component, config)` to create and show a dialog.
 *
 * @element kr-dialog
 *
 * @slot - Default slot for declarative dialog content (kr-dialog-header, kr-dialog-content, kr-dialog-footer)
 *
 * @fires close - Fired when a declarative dialog is closed
 */
export class KRDialog extends LitElement {
    constructor() {
        super(...arguments);
        this._dialogRef = null;
        this.opened = false;
        this.label = '';
        this.width = '560px';
        this._contentElement = null;
        this._handleDocumentKeyDown = (e) => {
            if (e.key === 'Escape') {
                this.close();
            }
        };
    }
    /**
     * Opens a dialog programmatically by creating a component and injecting it.
     */
    static open(component, config) {
        // Only remove other programmatic dialogs (those with a _dialogRef)
        document.querySelectorAll('kr-dialog').forEach((el) => {
            if (el._dialogRef) {
                el.remove();
            }
        });
        const dialogRef = new KRDialogRef();
        const dialog = document.createElement('kr-dialog');
        dialogRef.setDialogElement(dialog);
        dialog._dialogRef = dialogRef;
        // Create the content component
        const content = new component();
        content.dialogRef = dialogRef;
        if (config?.data) {
            content.data = config.data;
        }
        if (config?.label) {
            dialog.label = config.label;
        }
        if (config?.width) {
            dialog.width = config.width;
        }
        dialog._contentElement = content;
        dialog.opened = true;
        document.body.appendChild(dialog);
        return dialogRef;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._handleDocumentKeyDown);
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('opened')) {
            if (this.opened) {
                document.addEventListener('keydown', this._handleDocumentKeyDown);
            }
            else {
                document.removeEventListener('keydown', this._handleDocumentKeyDown);
            }
        }
    }
    /**
     * Opens the dialog (declarative mode).
     */
    open() {
        this.opened = true;
    }
    /**
     * Closes the dialog.
     * In programmatic mode (has _dialogRef), delegates to the dialog ref.
     * In declarative mode, sets opened=false and dispatches close event.
     */
    close() {
        if (this._dialogRef) {
            this._dialogRef.close(undefined);
            return;
        }
        this.opened = false;
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            composed: true
        }));
    }
    _handleBackdropClick(e) {
        if (e.target.classList.contains('backdrop')) {
            this.close();
        }
    }
    render() {
        return html `
      <div class="backdrop" @click=${this._handleBackdropClick}></div>
      <div class="dialog" style=${styleMap({ width: this.width })}>
        ${this.label
            ? html `
            <div class="dialog__header">
              <div class="dialog__header-label">${this.label}</div>
              <button
                class="dialog__close"
                aria-label="Close"
                @click=${this.close}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M6 18 18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          `
            : ''}
        <div class="dialog__body">${this._contentElement ? this._contentElement : html `<slot></slot>`}</div>
      </div>
    `;
    }
}
KRDialog.styles = css `
    :host {
      position: fixed;
      inset: 0;
      z-index: 10000;
      display: none;
      align-items: center;
      justify-content: center;
    }

    :host([opened]) {
      display: flex;
    }

    .backdrop {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }

    .dialog {
      position: relative;
      background: #fff;
      border: 1px solid rgba(0, 0, 0, 0.1);
      border-radius: 12px;
      box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
      max-height: 90vh;
      height: auto;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      width: 560px;
      max-width: calc(100vw - 48px);
    }

    ::slotted(kr-dialog-header),
    ::slotted(kr-dialog-footer) {
      flex-shrink: 0;
    }

    ::slotted(kr-dialog-content) {
      flex: 1 1 auto;
      overflow: auto;
    }

    .dialog__header {
      display: flex;
      padding: 0 24px;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      height: 64px;
      flex-shrink: 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    }

    .dialog__header-label {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
    }

    .dialog__close {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-right: -8px;
      padding: 0;
      background: none;
      border: none;
      border-radius: 6px;
      color: #1a1a2e;
      cursor: pointer;
      transition: background-color 0.15s, color 0.15s;
    }

    .dialog__close:hover {
      background: rgba(0, 0, 0, 0.06);
      color: #000000;
    }

    .dialog__close svg {
      width: 22px;
      height: 22px;
    }

    .dialog__body {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }
  `;
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRDialog.prototype, "opened", void 0);
__decorate([
    property({ type: String })
], KRDialog.prototype, "label", void 0);
__decorate([
    property({ type: String })
], KRDialog.prototype, "width", void 0);
__decorate([
    state()
], KRDialog.prototype, "_contentElement", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-dialog-header')) {
    customElements.define('kr-dialog-header', KRDialogHeader);
}
if (!customElements.get('kr-dialog-content')) {
    customElements.define('kr-dialog-content', KRDialogContent);
}
if (!customElements.get('kr-dialog-footer')) {
    customElements.define('kr-dialog-footer', KRDialogFooter);
}
if (!customElements.get('kr-dialog')) {
    customElements.define('kr-dialog', KRDialog);
}
//# sourceMappingURL=dialog.js.map